// Creatividades: the library of every creative the person may see (GET /orgs/:orgId/creatives — RLS and the
// Client/Brand scope are applied by the server), with search, filters, order, thumbnails and publication status.
// An organization without creatives gets a welcome: start with VALE, with a prebuilt format, or look at demos.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { CreativeDocument } from "@interactads/creative-model";
import { Film, LayoutGrid, Plus, Radio, Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, call } from "@/api/client";
import { useFrontDesk, usePaged } from "@/api/hooks";
import valeImage from "@/assets/vale/vale-256.jpg";
import { formatRelative } from "@/lib/format";
import { COMMON_FORMATS, PRODUCTS, VERSION_STATES } from "@/lib/labels";
import { Badge } from "@/ui/badge";
import { cn } from "@/ui/cn";
import { Button } from "@/ui/button";
import { Input, NativeSelect } from "@/ui/input";
import { EmptyState, PageHeader, Panel } from "@/ui/layout";
import { VersionStateBadge } from "@/components/status";
import { PageError, PageLoading, useTitle, useRouteParams } from "../common/page";
import { HierarchyFilters } from "../common/hierarchy-filters";
import { assetUrlFor } from "../studio/api";
import { Artboard } from "../studio/render/artboard";

const PAGE = 24;

interface Filters {
  q: string;
  clientId: string;
  brandId: string;
  campaignId: string;
  product: string;
  state: string;
  format: string;
  order: "newest" | "oldest";
}
const NO_FILTERS: Filters = {
  q: "",
  clientId: "",
  brandId: "",
  campaignId: "",
  product: "",
  state: "",
  format: "",
  order: "newest",
};
/** «Publicado» is not a version state: it means live in at least one Distribution. */
const LIVE = "LIVE";

function useLibrary(orgId: string, f: Filters) {
  const query = {
    limit: PAGE,
    order: f.order,
    ...(f.q ? { q: f.q } : {}),
    ...(f.clientId ? { clientId: f.clientId } : {}),
    ...(f.brandId ? { brandId: f.brandId } : {}),
    ...(f.campaignId ? { campaignId: f.campaignId } : {}),
    ...(f.product ? { product: f.product as never } : {}),
    ...(f.state === LIVE ? { published: true } : f.state ? { state: f.state as never } : {}),
    ...(f.format ? { format: f.format } : {}),
  };
  return usePaged(["org", orgId, "creative-library", query], (cursor) =>
    call(() =>
      api.GET("/orgs/{orgId}/creatives", {
        params: { path: { orgId }, query: { ...query, ...(cursor ? { cursor } : {}) } },
      })
    )
  );
}

export function CreativesPage() {
  const { orgId } = useRouteParams("orgId");
  const { data: fd, isLoading, error } = useFrontDesk(orgId);
  useTitle("Creatividades");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [typed, setTyped] = useState("");
  // Phones: the filters fold under one button so the creatives stay on the first screen.
  const [showFilters, setShowFilters] = useState(false);
  // The search is sent a moment after the person stops typing.
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => (f.q === typed.trim() ? f : { ...f, q: typed.trim() }));
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [typed]);
  const library = useLibrary(orgId, filters);
  const filtered = JSON.stringify({ ...filters, order: "newest" }) !== JSON.stringify(NO_FILTERS);
  if (isLoading) return <PageLoading />;
  if (error || !fd) return <PageError error={error} orgId={orgId} />;
  const base = `/o/${orgId}`;
  const canCreate = fd.capabilities.createCreative;
  // No creative at all (and nothing filtered): the welcome, not an empty table.
  if (!filtered && !library.isLoading && library.items.length === 0 && fd.recentCreatives.length === 0)
    return <Welcome base={base} canCreate={canCreate} />;

  const set = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
  };
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Creatividades"
        description="Todos los anuncios de tu espacio de trabajo que puedes ver."
        actions={
          canCreate ? (
            <Button asChild variant="primary">
              <Link to={`${base}/create`}>
                <Plus /> Crear creativo
              </Link>
            </Button>
          ) : null
        }
      />
      <Panel className="grid gap-3 p-3" data-testid="library-filters">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <Input
            aria-label="Buscar por nombre"
            placeholder="Buscar por nombre"
            className="pl-8"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
            }}
          />
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="justify-self-start sm:hidden"
          aria-expanded={showFilters}
          aria-controls="library-filter-fields"
          onClick={() => {
            setShowFilters((v) => !v);
          }}
        >
          <SlidersHorizontal aria-hidden="true" /> Filtros{filtered ? " (activos)" : ""}
        </Button>
        <div
          id="library-filter-fields"
          className={cn("grid-cols-1 gap-2 sm:grid sm:grid-cols-2 lg:grid-cols-4", showFilters ? "grid" : "hidden")}
        >
          <HierarchyFilters orgId={orgId} value={filters} onChange={set} />
          <NativeSelect
            aria-label="Tipo"
            value={filters.product}
            onChange={(e) => {
              set({ product: e.target.value });
            }}
          >
            <option value="">Todos los tipos</option>
            {Object.entries(PRODUCTS).map(([id, p]) => (
              <option key={id} value={id}>
                {p.label}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Estado"
            value={filters.state}
            onChange={(e) => {
              set({ state: e.target.value });
            }}
          >
            <option value="">Todos los estados</option>
            <option value={LIVE}>Publicado (entregando)</option>
            {Object.entries(VERSION_STATES).map(([id, s]) => (
              <option key={id} value={id}>
                Última versión: {s.label}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Formato"
            value={filters.format}
            onChange={(e) => {
              set({ format: e.target.value });
            }}
          >
            <option value="">Todos los formatos</option>
            {COMMON_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Orden"
            value={filters.order}
            onChange={(e) => {
              set({ order: e.target.value === "oldest" ? "oldest" : "newest" });
            }}
          >
            <option value="newest">Más recientes primero</option>
            <option value="oldest">Más antiguos primero</option>
          </NativeSelect>
        </div>
        {filtered ? (
          <div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setTyped("");
                setFilters({ ...NO_FILTERS, order: filters.order });
              }}
            >
              <X aria-hidden="true" /> Quitar filtros
            </Button>
          </div>
        ) : null}
      </Panel>

      {library.error ? (
        <PageError error={library.error} orgId={orgId} />
      ) : library.isLoading ? (
        <PageLoading />
      ) : library.items.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid />}
          title="Ninguna creatividad coincide"
          description="Prueba con otro nombre o quita algún filtro."
        />
      ) : (
        <>
          <ul
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
            aria-label="Creatividades"
            data-testid="library"
          >
            {library.items.map((c) => (
              <li key={c.id}>
                <CreativeCard orgId={orgId} base={base} c={c} />
              </li>
            ))}
          </ul>
          {library.hasNextPage ? (
            <Button
              variant="secondary"
              className="justify-self-center"
              disabled={library.isFetchingNextPage}
              onClick={() => void library.fetchNextPage()}
            >
              {library.isFetchingNextPage ? "Cargando…" : "Ver más"}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

type LibraryItem = ReturnType<typeof useLibrary>["items"][number];

function CreativeCard({ orgId, base, c }: { orgId: string; base: string; c: LibraryItem }) {
  return (
    <article
      className="grid h-full content-start gap-2 rounded-md border border-line bg-surface p-3 transition-colors hover:border-line-strong"
      aria-label={c.name}
      data-testid="library-card"
    >
      <Link to={`${base}/creatives/${c.id}`} className="block rounded-sm bg-sunken" tabIndex={-1} aria-hidden="true">
        <CreativeThumb orgId={orgId} item={c} />
      </Link>
      <div className="flex items-start justify-between gap-2">
        <Link to={`${base}/creatives/${c.id}`} className="text-sm font-semibold hover:underline">
          {c.name}
        </Link>
        {c.latestVersion ? <VersionStateBadge state={c.latestVersion.state} /> : null}
      </div>
      <p className="text-xs text-muted">
        {PRODUCTS[c.product].label}
        {c.formats.length > 0 ? ` · ${c.formats.join(" · ")}` : ""}
      </p>
      <p className="truncate text-xs text-muted" title={`${c.client.name} · ${c.brand.name} · ${c.campaign.name}`}>
        {c.brand.name} · {c.campaign.name}
      </p>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        {c.liveDistributions > 0 ? (
          <Badge tone="success">
            <Radio className="size-3" aria-hidden="true" /> Publicado en {c.liveDistributions}{" "}
            {c.liveDistributions === 1 ? "distribución" : "distribuciones"}
          </Badge>
        ) : (
          <span>Sin publicar</span>
        )}
        <span>· {formatRelative(c.lastActivityAt)}</span>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button asChild size="sm" variant="secondary">
          <Link to={`${base}/creatives/${c.id}`}>Abrir</Link>
        </Button>
        {c.latestVersion ? (
          <Button asChild size="sm" variant="ghost">
            <Link to={`${base}/versions/${c.latestVersion.id}/studio`}>Abrir en el Studio</Link>
          </Button>
        ) : null}
      </div>
    </article>
  );
}

/** Shows the version's first scene at rest once the card is on screen (one request per visible card). */
function CreativeThumb({ orgId, item }: { orgId: string; item: LibraryItem }) {
  const box = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el || seen) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [seen]);
  const versionId = item.latestVersion?.id;
  const version = useQuery({
    queryKey: ["org", orgId, "version", versionId],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/versions/{versionId}", { params: { path: { orgId, versionId: versionId ?? "" } } })
      ),
    enabled: seen && Boolean(versionId) && !item.html5,
  });
  const doc = useMemo(() => {
    const content = version.data?.content as Partial<CreativeDocument> | undefined;
    return content?.schemaVersion === 2 && content.layouts?.[0]?.sizing.mode === "fixed"
      ? (content as CreativeDocument)
      : null;
  }, [version.data]);
  const H = 150;
  const placeholder = (label: string, icon: React.ReactNode) => (
    <div className="grid h-full place-items-center gap-1 text-xs text-muted">
      <span className="grid place-items-center gap-1">
        {icon}
        {label}
      </span>
    </div>
  );
  let body: React.ReactNode;
  if (item.html5) body = placeholder("Anuncio HTML5", <Sparkles className="size-5" aria-hidden="true" />);
  else if (doc) {
    const layout = doc.layouts[0];
    const sizing = layout?.sizing;
    if (layout && sizing?.mode === "fixed") {
      const scale = Math.min(1, H / sizing.height, 240 / sizing.width);
      body = (
        <div className="grid h-full place-items-center">
          <div
            className="overflow-hidden rounded-sm shadow-sm"
            style={{ width: sizing.width * scale, height: sizing.height * scale }}
          >
            <div
              style={{
                width: sizing.width,
                height: sizing.height,
                transform: `scale(${String(scale)})`,
                transformOrigin: "0 0",
              }}
            >
              <Artboard doc={doc} layoutId={layout.id} mode="preview" assetUrl={assetUrlFor(orgId)} />
            </div>
          </div>
        </div>
      );
    }
  } else if (seen && !version.isLoading)
    body = placeholder(
      "Aún sin diseño",
      item.product === "VIDEO_RICH_MEDIA" ? (
        <Film className="size-5" aria-hidden="true" />
      ) : (
        <LayoutGrid className="size-5" aria-hidden="true" />
      )
    );
  return (
    <div ref={box} className="pointer-events-none p-2" style={{ height: H + 16 }} inert>
      {body ?? <div className="h-full animate-pulse rounded-sm bg-line/40" />}
    </div>
  );
}

/** First visit with no creative: what InteractAds does and the three ways to begin. */
function Welcome({ base, canCreate }: { base: string; canCreate: boolean }) {
  return (
    <div className="grid gap-6" data-testid="creatives-welcome">
      <PageHeader title="Creatividades" />
      <Panel className="grid items-center gap-6 p-6 md:grid-cols-[auto_minmax(0,1fr)]">
        <img
          src={valeImage}
          alt="VALE, la asistente creativa de InteractAds"
          className="mx-auto h-48 w-auto rounded-lg object-contain md:h-56"
        />
        <div className="grid gap-4">
          <div className="grid gap-1">
            <h2 className="text-xl font-semibold">Crea tu primer anuncio interactivo</h2>
            <p className="text-sm text-muted">
              Aquí verás todos tus anuncios. Empieza conversando con VALE o eligiendo un formato prediseñado: juegos,
              encuestas, video y banners que personalizas sin programar.
            </p>
          </div>
          <ol className="grid gap-2 text-sm sm:grid-cols-3">
            {[
              ["1. Crea", "Con VALE o con un formato listo para personalizar."],
              ["2. Publica", "Revisa, aprueba y elige la plataforma."],
              ["3. Usa tu Tag", "Cópialo en tu plataforma y mide los resultados aquí."],
            ].map(([title, text]) => (
              <li key={title} className="grid gap-0.5 rounded-md border border-line bg-sunken p-3">
                <span className="font-medium">{title}</span>
                <span className="text-muted">{text}</span>
              </li>
            ))}
          </ol>
          {canCreate ? (
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="primary">
                <Link to={`${base}/create`} search={{ start: "vale" }}>
                  <Sparkles aria-hidden="true" /> Crear con VALE
                </Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to={`${base}/create`} search={{ start: "prebuilt" }}>
                  <LayoutGrid aria-hidden="true" /> Ver formatos prediseñados y sus demostraciones
                </Link>
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted">Tu rol no permite crear creativos. Pide acceso a un administrador.</p>
          )}
        </div>
      </Panel>
    </div>
  );
}
